/**
 * Centralised risk-rating & severity-level configuration.
 *
 * Every label, color and score range is read from config.json so that a
 * single edit there propagates across the whole app.
 */
import config from "../config";

// ---------------------------------------------------------------------------
// Raw arrays (ordered highest → lowest, matching config.json)
// ---------------------------------------------------------------------------
export const riskRatings = config.riskRatings;
export const severityLevels = config.severityLevels;

// ---------------------------------------------------------------------------
// Derived helpers
// ---------------------------------------------------------------------------

/** Labels ordered highest → lowest: ["Level 4", "Level 3", ...] */
export const riskRatingLabels = riskRatings.map((r) => r.label);

/** Labels ordered highest → lowest: ["Critical", "Abnormal", ...] */
export const severityLabels = severityLevels.map((s) => s.label);

/** Score sub-labels for risk ratings: ["Score 23-25", ...] */
export const riskScoreLabels = riskRatings.map((r) => `Score ${r.scoreRange}`);

/** Score sub-labels for severity levels: ["Score 20-25", ...] */
export const severityScoreLabels = severityLevels.map(
    (s) => `Score ${s.scoreRange}`,
);

/** Map label → color: { "Level 4": "#EE3300", ... } */
export const riskColorMap = Object.fromEntries(
    riskRatings.map((r) => [r.label, r.color]),
);

/** Ordered color array (index 0 = highest risk): ["#EE3300", ...] */
export const riskCategoryColors = riskRatings.map((r) => r.color);

/**
 * Backward-compatible RISK_LEVELS object used by RiskRateSection,
 * Distribution, AssetDetails, etc.
 */
export const RISK_LEVELS = Object.fromEntries(
    riskRatings.map((r) => [r.label, { color: r.color, label: r.label }]),
);

/** Label of the highest risk (first entry). */
export const highestRiskLabel = riskRatings[0]?.label ?? "Level 4";

/** Fallback color for unknown risk values on the map. */
export const RISK_FALLBACK_COLOR = "#CCCCCC";

// ---------------------------------------------------------------------------
// Order array for sorting (highest → lowest)
// ---------------------------------------------------------------------------
export const riskRatesOrder = riskRatingLabels;

// ---------------------------------------------------------------------------
// CSS variable injection
// ---------------------------------------------------------------------------

function hexToRgba(hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Inject --risk-color-N and --risk-color-N-light CSS custom properties on
 * :root so that both CSS and JS share the same source of truth.
 *
 * The numbering follows the level-number convention already used in CSS:
 *   config[0] (highest, e.g. "Level 4") → --risk-color-{totalLevels}
 *   config[last] (lowest, e.g. "Level 1") → --risk-color-1
 */
export function injectRiskCssVariables() {
    const root = document.documentElement;
    const total = riskRatings.length;

    riskRatings.forEach((r, i) => {
        const n = total - i; // 4, 3, 2, 1 (for 4 levels)
        root.style.setProperty(`--risk-color-${n}`, r.color);
        root.style.setProperty(
            `--risk-color-${n}-light`,
            hexToRgba(r.color, 0.08),
        );
    });
}

/**
 * Build a Mapbox "match" expression that maps risk_rate property values to
 * their config colours.  Falls back to RISK_FALLBACK_COLOR.
 */
export function buildCircleColorExpression() {
    const expr = ["match", ["get", "risk_rate"]];
    riskRatings.forEach((r) => {
        expr.push(r.label, r.color);
    });
    expr.push(RISK_FALLBACK_COLOR);
    return expr;
}
